import { useEffect, useState } from 'react';
import { Box, Divider, IconButton, SvgIcon } from '@mui/material';
import { getUserType } from 'entities/user';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from 'shared/api';
import { useUser, queryClient } from 'shared/api/tanstack';
import AgentsIcon from 'shared/assets/icons/agents.svg';
import GuideIcon from 'shared/assets/icons/guid.svg';
import LeasingIcon from 'shared/assets/icons/leasing.svg';
import LogoutIcon from 'shared/assets/icons/logout.svg';
import SelectIcon from 'shared/assets/icons/select.svg';
import { ROUTE_PATHS } from 'shared/model/constants/paths';
import { LogoLink } from 'shared/ui/logo-link';
import styles from './dashboard-left-bar.module.scss';

const LeftBarSection = ({ title }: { title: string }) => (
  <div className={styles.sectionTitle}>{title}</div>
);

const LetfBarItem = ({
  title,
  count,
  icon,
  click,
}: {
  title: string;
  count: number;
  icon: SVGIcon;
  click: () => void;
}) => {
  return (
    <Box className={styles.agentsLink} onClick={click}>
      <SvgIcon
        component={icon}
        className={styles.svgLogout}
        inheritViewBox
        sx={{ fontSize: '18px' }}
      />
      <div className={styles.linkWrapper}>
        <span className={styles.spanText}>{title}</span>
        {!!count && <div className={styles.counter}>{count}</div>}
      </div>
    </Box>
  );
};

type LeadType = 'selection' | 'insurance' | 'leasing';

type LeadNewCounts = Record<LeadType, number>;

export const DashboardLeftBar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: user } = useUser();

  const [isAdmin, setIsAdmin] = useState(location.pathname.includes('/admin'));

  useEffect(() => {
    setIsAdmin(user?.isAdmin);
  }, [user]);

  const [leasingCount, setLeasingCount] = useState(0);
  const [selectCount, setSelectCount] = useState(0);
  const [leadNewCounts, setLeadNewCounts] = useState<LeadNewCounts>({
    selection: 0,
    insurance: 0,
    leasing: 0,
  });
  const [counter, setCounter] = useState<any>({
    agents: {
      new: 0,
      all: 1,
    },
    leasings: {
      all: 0,
      new: 0,
    },
    suppliers: {
      all: 0,
      new: 0,
    },
    clients: {
      all: 0,
      new: 0,
    },
  });

  const getGuide = (event: any) => {
    event?.preventDefault();
    const link = document.createElement('a');
    if (user?.agent) {
      link.href = '/Руководство_партнера.pdf';
      link.download = 'Руководство_партнера.pdf';
    } else {
      link.href = '/Руководство_пользователя.pdf';
      link.download = 'Руководство_пользователя.pdf';
    }
    link.click();
  };

  const fetchLeadNewCount = async (type: LeadType) => {
    const response = await apiClient.get('/leads', {
      params: {
        'filters[type][$eq]': type,
        'filters[lead_status][$eq]': 'new',
        'pagination[pageSize]': 1,
        'pagination[withCount]': true,
      },
    });

    return response.data.meta?.pagination?.total ?? 0;
  };

  const getCountApp = async () => {
    try {
      const response = await apiClient.get('/select-applications/count');
      const responseLeas = await apiClient.get('/leasing-applications/count');
      const responseAll = await apiClient.get('/select-applications/allCount');
      const [selectionNew, insuranceNew, leasingNew] = await Promise.all([
        fetchLeadNewCount('selection'),
        fetchLeadNewCount('insurance'),
        fetchLeadNewCount('leasing'),
      ]);

      setSelectCount(response?.data?.data?.count || 0);
      setLeasingCount(responseLeas?.data?.data?.count || 0);
      setLeadNewCounts({
        selection: selectionNew,
        insurance: insuranceNew,
        leasing: leasingNew,
      });

      if (responseAll?.data?.data?.users) {
        setCounter(responseAll.data?.data?.users);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const navigateToLeadsTab = (type: LeadType) => {
    navigate(`${ROUTE_PATHS.ADMIN_LEADS}?tab=${type}`);
  };

  useEffect(() => {
    if (isAdmin) {
      getCountApp();
    }
  }, [window.location.href]);

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    queryClient.clear();
    navigate('/auth');
  };

  return (
    <Box className={styles.leftBar}>
      <LogoLink dashboard admin={isAdmin} />

      <Box className={styles.adminLinks}>
        {isAdmin && (
          <>
            <LeftBarSection title='Заявки с сайта:' />
            <LetfBarItem
              title='Техника'
              count={leadNewCounts.selection}
              icon={SelectIcon}
              click={() => navigateToLeadsTab('selection')}
            />
            <LetfBarItem
              title='Лизинг'
              count={leadNewCounts.leasing}
              icon={LeasingIcon}
              click={() => navigateToLeadsTab('leasing')}
            />
            <LetfBarItem
              title='Страхование'
              count={leadNewCounts.insurance}
              icon={SelectIcon}
              click={() => navigateToLeadsTab('insurance')}
            />
            <Divider />
          </>
        )}

        <LeftBarSection title='Агентские заявки:' />
        <LetfBarItem
          title='Заявки на подбор'
          count={selectCount}
          icon={SelectIcon}
          click={() =>
            navigate(isAdmin ? '/dashboard/admin/all/select' : '/dashboard/all/select')
          }
        />
        <LetfBarItem
          title='Заявки на лизинг'
          count={leasingCount}
          icon={LeasingIcon}
          click={() =>
            navigate(isAdmin ? '/dashboard/admin/all/leasing' : '/dashboard/all/leasing')
          }
        />

        {isAdmin && (
          <>
            <Divider />
            <LeftBarSection title='Пользователи:' />
            <LetfBarItem
              title='Поставщики'
              count={counter.suppliers.new}
              icon={AgentsIcon}
              click={() => navigate('/dashboard/admin/users/suppliers')}
            />
            <LetfBarItem
              title='Лизинговые'
              count={counter.leasings.new}
              icon={AgentsIcon}
              click={() => navigate('/dashboard/admin/users/leasings')}
            />
            <LetfBarItem
              title='Агенты'
              count={counter.agents.new}
              icon={AgentsIcon}
              click={() => navigate('/dashboard/admin/users/agents')}
            />
            <LetfBarItem
              title='Клиенты'
              count={counter.clients.new}
              icon={AgentsIcon}
              click={() => navigate('/dashboard/admin/users/clients')}
            />
          </>
        )}

        <Divider />
        <LetfBarItem
          title='Рефералы'
          count={0}
          icon={SelectIcon}
          click={() => navigate(ROUTE_PATHS.REFERALS)}
        />

        {isAdmin && (
          <>
            <Divider />
            <LetfBarItem
              title='Каталог'
              count={0}
              icon={SelectIcon}
              click={() => navigate('/dashboard/admin/catalog')}
            />
          </>
        )}
      </Box>

      {user && (
        <div className={styles.userBlockWrapper}>
          {!isAdmin && (
            <Box className={styles.agentsLink} onClick={() => getGuide(event)}>
              <SvgIcon
                component={GuideIcon}
                className={styles.svgLogout}
                inheritViewBox
                sx={{ fontSize: '18px' }}
              />
              <span className={styles.spanText}>Инструкция</span>
            </Box>
          )}

          <Box className={styles.userBlock}>
            <Box
              className={styles.userInfo}
              onClick={() => navigate(ROUTE_PATHS.USER_PROFILE)}
              sx={{ cursor: 'pointer', flex: 1 }}
            >
              <span className={styles.userName}>
                {user.bio} ({getUserType(user)})
              </span>
              <span className={styles.userMail}>{user.email}</span>
            </Box>
            <IconButton
              onClick={logout}
              sx={{ width: '20px', height: '20px', marginRight: '-5px' }}
              className={styles.logoutBtn}
              edge='end'
              disableRipple
            >
              <SvgIcon
                component={LogoutIcon}
                className={styles.svgLogout}
                inheritViewBox
                sx={{ fontSize: '19px' }}
              />
            </IconButton>
          </Box>
        </div>
      )}
    </Box>
  );
};
